import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function money(value) {
  return Number(value || 0).toFixed(2);
}

function formatReceiptDate(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return [
    String(date.getUTCDate()).padStart(2, '0'),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    date.getUTCFullYear()
  ].join('-');
}

function formatDeliveryCompact(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}${iso[2]}${iso[1]}`;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return [
    String(date.getUTCDate()).padStart(2, '0'),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    date.getUTCFullYear()
  ].join('');
}

function masterName(items = []) {
  const names = [...new Set(items.map((item) => String(item.employeeName || '').trim()).filter(Boolean))];
  return names.join(', ');
}

function barcodeSvg(value) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svg, String(value), {
    format: 'CODE128',
    displayValue: false,
    margin: 0,
    width: 2,
    height: 56,
    background: '#ffffff',
    lineColor: '#000000'
  });
  return svg.outerHTML;
}

function labelCaption(item, billBookNo) {
  return [item.itemName, item.sizeName, item.colorName, billBookNo]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join('-');
}

/** QR payload: Product Name, Size, Color */
function qrPayload(item) {
  return [item.itemName, item.sizeName, item.colorName]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join('-');
}

function expandQrLabels(order) {
  const billBookNo = order.billBookNo || '';
  const labels = [];
  for (const item of order.items || []) {
    const qty = Math.max(0, Math.floor(Number(item.quantity) || 0));
    const caption = labelCaption(item, billBookNo);
    const payload = qrPayload(item);
    for (let i = 0; i < qty; i += 1) {
      labels.push({ caption, payload });
    }
  }
  return labels;
}

function printHtml(title, styles, bodyHtml) {
  return new Promise((resolve, reject) => {
    const win = window.open('', '_blank', 'noopener,noreferrer,width=420,height=720');
    if (!win) {
      reject(new Error('Popup blocked — allow popups to print'));
      return;
    }

    win.document.open();
    win.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)}</title>
  <style>${styles}</style>
</head>
<body>${bodyHtml}</body>
</html>`);
    win.document.close();

    const finish = () => {
      try {
        win.focus();
        win.print();
      } finally {
        setTimeout(() => {
          try { win.close(); } catch { /* ignore */ }
          resolve();
        }, 400);
      }
    };

    if (win.document.readyState === 'complete') {
      setTimeout(finish, 200);
    } else {
      win.onload = () => setTimeout(finish, 200);
    }
  });
}

const receiptStyles = `
  @page { size: 76mm auto; margin: 2mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 12px;
    color: #000;
    background: #fff;
  }
  .receipt {
    width: 72mm;
    margin: 0 auto;
    padding: 2mm;
  }
  .meta { line-height: 1.45; margin-bottom: 6px; }
  .meta div { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  thead th {
    border-top: 1px solid #000;
    border-bottom: 1px solid #000;
    padding: 3px 1px;
    font-size: 11px;
    text-align: left;
  }
  tbody td {
    padding: 4px 1px;
    vertical-align: top;
    font-size: 11px;
    word-break: break-word;
  }
  tbody tr:last-child td { border-bottom: 1px solid #000; padding-bottom: 6px; }
  .col-item { width: 38%; }
  .col-size { width: 16%; }
  .col-qty { width: 14%; text-align: right; }
  .col-rate { width: 32%; text-align: right; }
  .item-name { font-weight: 700; display: block; }
  .item-color { display: block; }
  .total-row {
    display: flex;
    justify-content: space-between;
    font-weight: 700;
    font-size: 13px;
    margin: 8px 0 10px;
  }
  .barcode {
    text-align: center;
    margin-top: 4px;
  }
  .barcode svg { max-width: 100%; height: auto; }
  .barcode-text {
    margin-top: 4px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
`;

const labelStyles = `
  @page { size: 50mm 40mm; margin: 0; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: Arial, Helvetica, sans-serif;
    color: #000;
    background: #fff;
  }
  .label {
    width: 50mm;
    height: 40mm;
    padding: 2.5mm;
    page-break-after: always;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    overflow: hidden;
  }
  .label:last-child { page-break-after: auto; }
  .caption {
    width: 100%;
    text-align: center;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 2mm;
    word-break: break-word;
  }
  .qr img {
    width: 24mm;
    height: 24mm;
    display: block;
  }
`;

export async function printOrderReceipt(order) {
  const items = order.items || [];
  const total = items.reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.masterRate || 0),
    0
  );
  const orderNo = order.orderNumber || '';
  const barcode = barcodeSvg(orderNo);

  const rows = items.map((item) => `
    <tr>
      <td class="col-item">
        <span class="item-name">${escapeHtml(item.itemName || '')}</span>
        <span class="item-color">${escapeHtml(item.colorName || '')}</span>
      </td>
      <td class="col-size">${escapeHtml(item.sizeName || '')}</td>
      <td class="col-qty">${escapeHtml(item.quantity)}</td>
      <td class="col-rate">${escapeHtml(money(item.masterRate))}</td>
    </tr>
  `).join('');

  const body = `
    <div class="receipt">
      <div class="meta">
        <div>Manual Order #: ${escapeHtml(order.billBookNo || '')}</div>
        <div>Date: ${escapeHtml(formatReceiptDate(order.orderDate))}</div>
        <div>Master: ${escapeHtml(masterName(items))}</div>
        <div>Delivery Date: ${escapeHtml(formatDeliveryCompact(order.deliveryDate))}</div>
      </div>
      <table>
        <thead>
          <tr>
            <th class="col-item">Item Color</th>
            <th class="col-size">Size</th>
            <th class="col-qty">QTY</th>
            <th class="col-rate">Rate</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="total-row">
        <span>Total:</span>
        <span>${escapeHtml(money(total))}</span>
      </div>
      <div class="barcode">
        ${barcode}
        <div class="barcode-text">${escapeHtml(orderNo)}</div>
      </div>
    </div>
  `;

  await printHtml(`Order ${orderNo}`, receiptStyles, body);
}

export async function printOrderQrLabels(order) {
  const labels = expandQrLabels(order);
  if (!labels.length) return;

  const parts = await Promise.all(labels.map(async (label) => {
    const dataUrl = await QRCode.toDataURL(label.payload, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 180,
      color: { dark: '#000000', light: '#ffffff' }
    });
    return `
      <div class="label">
        <div class="caption">${escapeHtml(label.caption)}</div>
        <div class="qr"><img src="${dataUrl}" alt="QR" /></div>
      </div>
    `;
  }));

  await printHtml(`QR Labels ${order.orderNumber || ''}`.trim(), labelStyles, parts.join(''));
}

export async function printOrderOnSave(order) {
  await printOrderReceipt(order);
  await printOrderQrLabels(order);
}
